import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ErrorState, LoadingState, Notice, PageHeader, Spinner } from "@/components/common/States";
import { InviteLinkBox } from "@/components/common/InviteLinkBox";
import { InvitationTable } from "@/components/common/InvitationTable";
import { useAuth, useSchoolCan, useSpaceName } from "@/context/AuthContext";
import { api, errorMessage, fetcher } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/labels";
import { ReadOnlyBanner } from "./SchoolHome";

export default function SchoolInvitations() {
  const { me } = useAuth();
  const space = useSpaceName();
  const canCreate = useSchoolCan("invitations:create");
  const roles = me.grantable_roles;
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["school-invitations"], queryFn: () => fetcher("/school/invitations") });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setErr("Format email tidak valid.");
    if (!role) return setErr("Pilih peran untuk undangan.");
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/school/invitations", { email: email.trim(), role });
      setCreated({ token: data.token, email: data.invitation.email });
      setEmail("");
      qc.invalidateQueries();
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (inv, reason) => {
    await api.post(`/school/invitations/${inv.id}/revoke`, { reason });
    toast.success("Undangan dicabut.");
    qc.invalidateQueries();
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={space} title="Undangan Pengguna" description="Undangan bersifat sekali pakai, memiliki masa berlaku, dan dapat dicabut. Penerima membuat kata sandinya sendiri." />
      <ReadOnlyBanner />
      {canCreate && (
        <form onSubmit={submit} className="flex flex-col md:flex-row gap-2" noValidate data-testid="school-invite-form">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email penerima" aria-label="Email penerima" data-testid="school-invite-email-input" />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="md:w-56" aria-label="Peran" data-testid="school-invite-role-select"><SelectValue placeholder="Pilih peran" /></SelectTrigger>
            <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="submit" disabled={busy} data-testid="school-invite-submit-button">{busy && <Spinner />} Buat tautan undangan</Button>
        </form>
      )}
      {err && <Notice tone="danger" testId="school-invite-error">{err}</Notice>}
      {created && <InviteLinkBox token={created.token} email={created.email} />}
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : (
        <InvitationTable items={q.data} onRevoke={revoke} canRevoke={(i) => canCreate && roles.includes(i.role)} />
      )}
    </div>
  );
}
